<template>
  <div
    id="metric_form"
    tabindex="-1"
    :class="classes"
    class="relative w-full px-6 pt-3 pb-1 mt-4 h-fit rounded-md shadow-sm select-none !focus:ring-0 bg-light-box-background dark:bg-dark-box-background dark:border-dark-divider-border dark:focus:ring-dark-brand-blue sm:text-sm">
    <div v-if="formulary_clone.type == consts.FORMULA_TYPES.CASES">
      <ul role="list" class="border-1">
        <li
          v-for="(case_item, index) in formulary_clone.cases"
          :key="case_item.key"
          class="relative z-10 flex flex-col px-2 py-2 sm:px-0 border-1">
          <div class="relative flex flex-col gap-2 p-2 rounded-md border border-[#FF767E] border-dashed bg-[#FF767E10] ring-0 ring-blue">
            <div class="flex relative z-10 space-x-6 items-center bg-[#FF767E] rounded-full h-10">
              <label for="when" class="block text-white border-transparent text-sm font-medium sm:mt-px ml-2 dark:text-gray-50">If</label>
              <ExpressionHolder
                v-if="show_expression_holder"
                v-model:active-when-then-cursor="active_when_then_cursor"
                :show-dropdown-active="showDropdownActive"
                :object-type="objectType"
                :is-when="true"
                expression-type="filter"
                :ref-id="`${index}-when`"
                :expression="case_item.when"
                class="bg-white dark:bg-dark-box-background w-14 h-20 rounded-full"
                @change="updateWhenCase($event, index)"
                @valid="$emit('valid', $event)" />
            </div>
            <div class="flex space-x-6 items-center bg-[#FF767E] rounded-full h-10">
              <label for="then" class="block text-sm font-medium sm:mt-px dark:text-gray-50 ml-2 text-white">Then</label>
              <ExpressionHolder
                v-if="show_expression_holder"
                v-model:active-when-then-cursor="active_when_then_cursor"
                :object-type="objectType"
                :ref-id="`${index}-then`"
                expression-type="value"
                :show-dropdown-active="showDropdownActive"
                :when-first-metric="formulary_clone.cases[0].when.find((x) => x.type == 'metric')"
                :expression="case_item.then"
                class="bg-white dark:bg-dark-box-background w-14 h-5 rounded-full"
                @change="updateThenCase($event, index)"
                @valid="$emit('valid', $event)" />
            </div>
            <CircleX
              class="absolute w-6 h-6 text-red-500 bg-white border rounded-full cursor-pointer group-hover:bg-gray-100 hover:opacity-100 opacity-30 dark:border-dark-divider-border -top-1 -right-1"
              @click="
                () => {
                  formulary_clone.cases.splice(index, 1)
                  formulary_clone.cases.length == 0
                    ? (formulary_clone = { expression: [], type: 'value', cases: [], default: null })
                    : () => {}
                }
              " />
          </div>
        </li>

        <div
          class="float-right py-2 ml-4 mb-0 cursor-pointer w-8 h-8 ignore whitespace-nowrap flex items-center justify-center bg-[#FF767E10] p-1 border border-[#FF767E] border-dashed rounded-full relative z-1"
          @click="formulary_clone.cases.push({ when: [], then: [] })">
          <Plus class="w-6 h-6 text-[#FF767E]" />
        </div>
        <div class="py-4 mt-2 relative bottom-6 space-y-6 sm:space-y-5 ignore mr-4">
          <div class="flex space-x-6 items-center bg-[#FF767E] rounded-full h-10">
            <label for="def" class="block text-sm font-medium sm:mt-px dark:text-gray-50 ml-2 text-white">Default</label>
            <ExpressionHolder
              v-if="show_expression_holder"
              v-model:active-when-then-cursor="active_when_then_cursor"
              :object-type="objectType"
              expression-type="value"
              :show-dropdown-active="showDropdownActive"
              :ref-id="`default`"
              :expression="formulary_clone.default"
              :when-first-metric="formulary_clone.cases[0].when.find((x) => x.type == 'metric')"
              class="bg-white dark:bg-dark-box-background w-14 h-5 rounded-full"
              @change="updateCaseDefault($event)"
              @valid="$emit('valid', $event)" />
          </div>
        </div>
      </ul>
    </div>
    <div v-else>
      <ExpressionHolder
        v-if="show_expression_holder"
        :object-type="objectType"
        :expression="formulary_clone.expression"
        :show-dropdown-active="showDropdownActive"
        tabindex="0"
        expression-type="value"
        @updateFormulaType="
          () => {
            formulary_clone.cases = [{ when: [], then: [] }]
            formulary_clone.default = []
            formulary_clone.type = consts.FORMULA_TYPES.CASES
          }
        "
        @change="updateExpression($event)"
        @valid="$emit('valid', $event)" />
    </div>
  </div>
</template>

<script>
import { consts } from "@/store/constants"
import { utils } from "@/store/helpers"
import ExpressionHolder from "./ExpressionHolder.vue"
import { Plus, CircleX } from "lucide-vue-next"

import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    ExpressionHolder,
    CircleX,
    Plus
  },
  props: {
    clearFormulaTrigger: null,
    objectType: {
      type: String,
      default: null,
      required: false
    },
    formulary: {
      type: Object,
      default: () => {}
    },
    classes: {
      type: String,
      default: ""
    },
    showDropdownActive: null
  },
  emits: ["update:isValid", "update:errors", "errors", "isValid", "change", "valid"],
  data() {
    return {
      consts,
      all_options: [],
      formulary_clone: { expression: [], type: "value", cases: [], default: [] },
      copy_formulary_clone: { type: "cases", cases: [], default: [] },
      is_drag_on: false,
      active_when_then_cursor: "0-when",
      show_expression_holder: true
    }
  },
  watch: {
    clearFormulaTrigger() {
      this.formulary_clone = { expression: [], type: "value", cases: [], default: [] }
      this.$emit("change", this.formulary_clone)
      this.show_expression_holder = false
      setTimeout(() => {
        this.show_expression_holder = true
      }, 100)
    },
    formulary_clone: {
      deep: true,
      immediate: true,
      handler() {
        const formula_clone_obj = this.$utils.clone(this.formulary_clone)
        if (!formula_clone_obj["type"]) {
          formula_clone_obj["type"] = "value"
        }
        this.$emit("change", formula_clone_obj)
      }
    }
  },

  created() {
    this.register_listeners()

    let forecasting_mappings_metrics = []
    let forecasting_mappings =
      utils.find(this.$store.state.settings.modules_settings, { reference_code: "forecasting_mappings" })?.forecasting_mappings || []
    forecasting_mappings.forEach((m) => {
      forecasting_mappings_metrics.push({
        key: utils.generateRandomKey(),
        type: "forecast_metrics",
        object_type: utils.getObjectTypeById(m.object_type)?.type,
        data_type: m.data_type,
        name: `Renewal Base (${m.name})`,
        display: `Renewal Base (${m.name})`,
        reference_name: `${m.reference_name}_renewal_base`,
        value: `${m.reference_name}_renewal_base`,
        label: `Renewal Base (${m.name})`
      })
      forecasting_mappings_metrics.push({
        key: utils.generateRandomKey(),
        type: "forecast_metrics",
        object_type: m.object_type,
        data_type: m.data_type,
        name: `Renewal Date (${m.name})`,
        display: `Renewal Date (${m.name})`,
        reference_name: `${m.reference_name}_renewal_date`,
        value: `${m.reference_name}_renewal_date`,
        label: `Renewal Date (${m.name})`
      })
    })
    let global_variables =
      utils.find(this.$store.state.settings.modules_settings, { reference_code: "global_variables" })?.global_variables || []
    global_variables = global_variables.map((m) => {
      m["display"] = `${m.name} (${m.values.find((x) => x.user == this.$store.state.auth.user.id)?.value})`
      m["label"] = m.name
      m["key"] = utils.generateRandomKey()
      m["data_type"] = m.input_type

      m["value"] = "global_variables_" + m.reference_name
      m["type"] = "global_variable"
      return m
    })
    this.all_options = consts.FORMULARY_OPTIONS.concat(forecasting_mappings_metrics)
      .concat(global_variables)
      .concat(
        this.$store.state.metrics.map((m) => {
          m["display"] = m.name
          m["label"] = m.name
          m["value"] = m.reference_name
          m["type"] = "metric"
          return m
        })
      )
      .map((m) => {
        return utils.setRandomKey(m)
      })

    if (utils.isNotEmpty(this.formulary)) {
      this.formulary_clone = this.$utils.clone(this.formulary)
    }
  },
  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.DRAG_IN_ON_FORMULAT_HOLDER, (is_on) => {
        this.is_drag_on = is_on
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.DRAG_IN_ON_FORMULAT_HOLDER)
    },
    updateWhenCase(exp, index) {
      delete this.formulary_clone["expression"]
      let parsed = this.$utils.clone(this.formulary_clone)
      parsed.cases[index].when = this.parseForServer(exp)

      if (this.copy_formulary_clone["cases"].length - 1 < index) {
        this.copy_formulary_clone["cases"].push({ when: parsed.cases[index].when, then: null })
      } else {
        this.copy_formulary_clone["cases"][index]["when"] = parsed.cases[index].when
      }

      this.$emit("change", this.copy_formulary_clone)
    },
    updateThenCase(exp, index) {
      delete this.formulary_clone["expression"]
      let parsed = this.$utils.clone(this.formulary_clone)
      parsed.cases[index].then = this.parseForServer(exp)

      this.copy_formulary_clone["cases"][index]["then"] = parsed.cases[index].then
      if (this.copy_formulary_clone["cases"].length - 1 < index) {
        this.copy_formulary_clone["cases"].push({ then: parsed.cases[index].then, when: null })
      } else {
        this.copy_formulary_clone["cases"][index]["then"] = parsed.cases[index].then
      }
      this.$emit("change", this.copy_formulary_clone)
    },
    updateCaseDefault(exp) {
      delete this.formulary_clone["expression"]
      let parsed = this.$utils.clone(this.formulary_clone)
      parsed.default = this.parseForServer(exp)

      this.copy_formulary_clone["default"] = parsed.default
      this.$emit("change", this.copy_formulary_clone)
    },
    updateExpression(exp) {
      delete this.formulary_clone["cases"]
      delete this.formulary_clone["default"]
      let parsed = this.$utils.clone(this.formulary_clone)
      parsed.expression = this.parseForServer(exp)
      this.formulary_clone.expression = exp
      if (!parsed["type"]) {
        parsed["type"] = "value"
      }
      this.$emit("change", parsed)
    },
    parseForServer(exp) {
      let list = []

      if (Array.isArray(exp)) {
        exp.forEach((v, index) => {
          if (exp[index]?.type == "input") {
            if (exp[index]?.data_type == "Boolean") {
              list.push(v.value ? "True" : "False")
            } else if (exp[index].data_type == "List") {
              list.push("[")
              if (v.value) {
                list.push(v.value.map((x) => `'${x}'`).join(","))
              }
              list.push("]")
            } else if (utils.isNumber(exp[index].value)) {
              list.push(String(v.value).replace(",", ""))
            } else {
              if (v?.display == "In Date Range") {
                list.push("(")
                if (typeof v.value.start == "string") {
                  list.push(`'${v.value.start}'`)
                } else {
                  list.push(`'${v.value.start?.toISOString()}'`)
                }

                list.push(",")
                if (typeof v.value.end == "string") {
                  list.push(`'${v.value.end}'`)
                } else {
                  list.push(`'${v.value.end?.toISOString()}'`)
                }
                list.push(")")
              } else if (v?.display == "In Range") {
                list.push("(")
                list.push(String(v.value.start).replace(",", ""))
                list.push(",")
                list.push(String(v.value.end).replace(",", ""))
                list.push(")")
              } else if (
                exp[index - 1]?.type == "operator" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                (exp[index - 2]?.data_type == "Date" || exp[index - 2]?.data_type == "DateTime") &&
                exp[index - 1]?.value == "=="
              ) {
                list.push(`'${v.value.toISOString()}'`)
              } else if (
                exp[index - 1]?.type == "operator" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                exp[index - 2]?.data_type == "Boolean"
              ) {
                list.push(v.value ? "True" : "False")
              } else if (
                exp[index - 1]?.type == "operator" &&
                exp[index - 1]?.value == "in" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                exp[index - 2]?.data_type == "Number"
              ) {
                list.push("[")
                if (v.value) {
                  list.push(v.value.join(","))
                }
                list.push("]")
              } else if (
                exp[index - 1]?.type == "operator" &&
                exp[index - 1]?.value == "in" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                exp[index - 2]?.data_type == "String"
              ) {
                list.push("[")
                if (v.value) {
                  list.push(
                    v.value
                      .split(",")
                      .map((x) => `'${x}'`)
                      .join(",")
                  )
                }
                list.push("]")
              } else {
                list.push(`'${v.value}'`)
              }
            }
          } else {
            if (v) {
              list.push(v?.value)
            }
          }
        })
        return list.join(" ")
      } else {
        return exp
      }
    }
  }
}
</script>
